import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { topMisconfigurationColumnDefs as baseColumnDefs } from "../col_defs";
import { TABLE_INFO } from "../shared/table_config";
import SkeletonLoader from "../../../modules/dashboard/shared/skeleton_loader";
import useDashboardService from "../services";
import { toast } from "react-toastify";
import {
  SEVERITY_FILTER_OPTIONS as filterOptions,
  DEFAULT_SEVERITY_FILTER as defaultFilter,
} from "../shared/constants";

import eventEmitter from "../../../components/utils/eventEmitter";
import {
  Box,
  Typography,
  Chip,
  useTheme,
  Drawer,
  Divider,
  Tooltip,
  Popover,
  TextField,
  Button,
  Tabs,
  Tab,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import AgGridTable from "../../../components/table/AgGridTable";
// import SortDropdown from "../shared/sortdropdown"; // Disabled - Backend handles filtering
import styles from "../shared/shared.module.scss";
import agGridStyles from "../shared/ag_grid_styles.module.scss";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Pencil_Blue from "../../../assets/baseVersion/Pencil_Blue.svg";
import Pencil_Light_img from "../../../assets/baseVersion/Pencil_Light_img.svg";
import Logoutput from "../../../pages/SecurityPosture/Logoutput";
import { useFormik } from "formik";
import SearchIcon from "@mui/icons-material/Search";
import Plus_Blue_img from "../../../assets/baseVersion/Plus_Blue_img.svg";
import {
  AssetsFlyout,
  Compliance,
  DetailsFlyout,
} from "../../../pages/SecurityPosture";
import {
  setSelectedRowID,
  setTabSelected,
} from "../../../pages/SecurityPosture/TabSelection-slice";
import { useDispatch } from "react-redux";
import AntSwitch from "../../../components/AntSwitch";
import React from "react";
import { useDashboardContext } from "../DashboardContext";

const PAGE_SIZE = 10;
const SCROLL_THROTTLE_MS = 500;

interface DeviceInfo {
  count: number;
  device: string;
}

interface DataProps {
  count: number;
  id: string;
  hover: DeviceInfo[][];
  parameter: string;
  failed_count: number;
  vulnerability: string;
}

interface CreateTag {
  default?: string[];
  user?: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SelectedRowData {
  id: string;
  normalized_name?: string;
  severity?: string;
  type?: string;
  [key: string]: any;
}

interface TopMisconfigurationProps {
  isbothCloudAndPremAssetUser: boolean;
  isOnlyPremAssetUser: boolean;
  isOnlyCloudUser: boolean;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const TopMisconfiguration = ({
  isbothCloudAndPremAssetUser: _isbothCloudAndPremAssetUser,
  isOnlyPremAssetUser: _isOnlyPremAssetUser,
  isOnlyCloudUser: _isOnlyCloudUser,
}: TopMisconfigurationProps) => {
  const { fetchTopMisconfigurations } = useDashboardService();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { setIsOverlayOpen } = useDashboardContext();

  const [misConfigData, setMisconfigData] = useState<DataProps[]>([]);
  const [isCloud, setIsCloud] = useState<boolean | null>(null);
  const [switchDisabled, setSwitchDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [filterBy, setFilterBy] = useState(defaultFilter);
  const [trigger, setTrigger] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SelectedRowData | null>(null);
  const [isCreateTag, setIsCreateTags] = useState<CreateTag>({});
  const [showLogOutput, setShowLogOutput] = useState(false);
  const [headerResponse, setHeaderResponse] = useState<any>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreateTagClicked, setIsCreateTagClicked] = useState(false);
  const [logOutputRowData, setLogOutputRowData] = useState<any>(null);
  const [value, setValue] = useState(0);
  const [isDetailsTotalResources, setIsDetailsTotalResources] =
    useState<any>(null);
  const [isDrawerDataLoading, setIsDrawerDataLoading] = useState(false);
  const [, setRiskDetails] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasNext: false,
    hasPrevious: false,
  });

  const gridRef = useRef<any>(null);
  const isFetchingNext = useRef(false);
  const isFetchingPrev = useRef(false);
  const isCloudRef = useRef<boolean | null>(isCloud);
  const lastScrollTs = useRef<number>(0);
  // Track first visible row index and pixel scroll position
  const firstVisibleRowRef = useRef<number>(0);
  const scrollPositionRef = useRef<number>(0);
  const scrollCount = useRef(0); // Count scroll events

  // Update ref when isCloud changes
  useEffect(() => {
    isCloudRef.current = isCloud;
  }, [isCloud]);

  // Notify parent context when drawer opens/closes to disable drag and drop
  useEffect(() => {
    setIsOverlayOpen(isDrawerOpen);
  }, [isDrawerOpen, setIsOverlayOpen]);

  useEffect(() => {
    const handleCloseAllDrawers = () => {
      setIsDrawerOpen(false);
    };
    eventEmitter.on("closeAllDrawers", handleCloseAllDrawers);
    return () => {
      eventEmitter.off("closeAllDrawers", handleCloseAllDrawers);
    };
  }, []);

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  // Initialize cloud/prem toggle
  useEffect(() => {
    if (_isOnlyCloudUser) {
      setIsCloud(true);
      setSwitchDisabled(true);
    } else if (_isOnlyPremAssetUser) {
      setIsCloud(false);
      setSwitchDisabled(true);
    } else {
      setSwitchDisabled(false);
      setIsCloud(false);
    }
  }, [_isOnlyCloudUser, _isOnlyPremAssetUser]);

  // Dynamic column definitions based on cloud/on-prem toggle
  const columnDefs = useMemo(() => {
    const { HEADER_LABEL } = TABLE_INFO.TOP_MISCONFIGURATIONS;
    return baseColumnDefs.map((col: any) => {
      // Update the Asset/Resource column header based on isCloud state
      if (col.field === "count") {
        return {
          ...col,
          headerName: isCloud ? HEADER_LABEL.RESOURCES : HEADER_LABEL.ASSETS,
        };
      }
      return col;
    });
  }, [isCloud]);

  // Fetch control details
  const fetchControlDetails = useCallback(async () => {
    if (!selectedRow?.id) return;
    setIsDrawerDataLoading(true);
    try {
      const response = await axiosInstance.get(
        `securityscan/control-details/${selectedRow.id}`
      );
      setHeaderResponse(response.data?.data);
      setIsCreateTags(response.data?.data?.tags);
    } catch (error) {
      console.error("Error fetching details:", error);
    } finally {
      setIsDrawerDataLoading(false);
    }
  }, [selectedRow?.id]);

  // Fetch risk details
  const fetchRiskDetails = useCallback(async () => {
    if (isCloud === null) return;
    const module = isCloud ? "cloud" : "on_premise";
    setIsDrawerDataLoading(true);
    try {
      const response = await axiosInstance.get(
        END_POINTS.RISK_ACCEPTANCE_TABLE,
        {
          params: { module },
        }
      );
      setRiskDetails(response.data.data);
    } catch (error) {
      console.error("Risk Details Data Fetch Failed", error);
    } finally {
      setIsDrawerDataLoading(false);
    }
  }, [isCloud]);

  // Fetch total resources
  useEffect(() => {
    if (!selectedRow?.id || isCloud === null) return;

    const module = isCloud ? "cloud" : "on_premise";
    axiosInstance
      .get(
        `securityscan/control-asset-details/${selectedRow.id}?module=${module}`
      )
      .then((response) => setIsDetailsTotalResources(response.data))
      .catch((error) => console.error("Error fetching details:", error));
  }, [selectedRow?.id, isCloud]);

  // Handle filter saved event
  useEffect(() => {
    const handleFilterSaved = () => {
      setLoading(true);
      setTrigger((prev) => prev + 1);
    };

    eventEmitter.on("filterSaved", handleFilterSaved);

    return () => {
      eventEmitter.off("filterSaved", handleFilterSaved);
    };
  }, []);

  // Fixed fetchData function
  const fetchData = useCallback(
    async ({
      page = 1,
      mode = "initial",
    }: {
      page?: number;
      mode?: "initial" | "append" | "prepend";
    } = {}) => {
      const currentIsCloud = isCloudRef.current;
      if (currentIsCloud === null) return;

      // Capture current first visible row and pixel scroll before fetching
      try {
        const apiBefore = gridRef.current?.api;
        if (apiBefore && mode !== "initial") {
          // Save the first displayed row index so we can restore it later
          firstVisibleRowRef.current =
            typeof apiBefore.getFirstDisplayedRow === "function"
              ? apiBefore.getFirstDisplayedRow()
              : firstVisibleRowRef.current;

          // As a fallback, also store top pixel position
          const vRangeBefore = apiBefore.getVerticalPixelRange?.();
          scrollPositionRef.current = vRangeBefore?.top ?? scrollPositionRef.current;
        }
      } catch (err) {
        console.warn("Could not capture pre-fetch scroll state", err);
      }

      if (mode === "initial") {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        const module = currentIsCloud ? "cloud" : "on_premise";
        const payload = { type: 1 };

        const {
          data,
          pagination: pageInfo,
          error,
        } = await fetchTopMisconfigurations(payload, module, {
          page,
          page_size: PAGE_SIZE,
          sort_by: "severity",
          sort_order: "desc",
        });

        // Debug: log API response
        console.log("📥 API Response:", {
          module,
          page,
          dataType: Array.isArray(data) ? "array" : typeof data,
          dataLength: Array.isArray(data) ? data.length : "N/A",
          dataSample: Array.isArray(data) && data.length > 0 ? data[0] : data,
          pageInfo,
          error,
        });

        if (error) {
          toast.error((error as any)?.message || "Failed to load misconfigurations");
          return;
        }

        setPagination({
          page: pageInfo?.page || page,
          totalPages: pageInfo?.total_pages || 1,
          hasNext: pageInfo?.has_next || false,
          hasPrevious: pageInfo?.has_previous || false,
        });

        setMisconfigData((prev) => {
          if (mode === "initial") {
            console.log("✅ Setting initial data:", data?.length ?? 0, "items");
            return data || [];
          }

          // NOTE: We intentionally do NOT filter out duplicate IDs here.
          // The goal is to display all records returned by the backend, even
          // if pagination returns overlapping items. If you need to restore
          // duplicate filtering in the future, uncomment the original logic
          // below.
          // const existingIds = new Set(prev.map((item) => item.id));
          // const newItems = (data || []).filter(
          //   (item: DataProps) => !existingIds.has(item.id)
          // );
          // const duplicateCount = (data || []).length - newItems.length;

          const itemsToAdd = data || [];

          if (mode === "append") {
            console.log(
              "➕ Appending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing (duplicates not filtered)"
            );
            return [...prev, ...itemsToAdd];
          }
          if (mode === "prepend") {
            console.log(
              "⬆️ Prepending:",
              itemsToAdd.length,
              "items to",
              prev.length,
              "existing (duplicates not filtered)"
            );
            return [...itemsToAdd, ...prev];
          }

          return prev;
        });

        // Restore scroll position after DOM updates. Using ensureIndexVisible
        // based on the previously captured first visible row is more reliable
        // than attempting to set pixel offsets directly.
        try {
          const apiAfter = gridRef.current?.api;
          const itemsToAdd = data || [];
          if (apiAfter) {
            if (mode === "append") {
              // Ensure previously top row is visible at top
              const indexToShow = firstVisibleRowRef.current ?? 0;
              setTimeout(() => {
                apiAfter.ensureIndexVisible?.(indexToShow, "top");
              }, 50);
            } else if (mode === "prepend") {
              // When prepending, the previously visible row index shifts by
              // the number of new items added at the top.
              const indexToShow = (firstVisibleRowRef.current ?? 0) + itemsToAdd.length;
              setTimeout(() => {
                apiAfter.ensureIndexVisible?.(indexToShow, "top");
              }, 50);
            }
          }
        } catch (err) {
          console.warn("Could not restore scroll after data update", err);
        }
      } catch (error) {
        console.error("Error fetching:", error);
        toast.error("Error fetching misconfigurations");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        isFetchingNext.current = false;
        isFetchingPrev.current = false;
      }
    },
    [fetchTopMisconfigurations]
  );

  // Initial fetch - FIXED: removed fetchData from dependencies
  useEffect(() => {
    if (isCloud === null) return;

    // Sync ref so fetchData can read latest value
    isCloudRef.current = isCloud;

    scrollCount.current = 0; // Reset scroll counter
    isFetchingNext.current = false; // Reset fetching flags
    isFetchingPrev.current = false;

    setMisconfigData([]);
    setPagination({
      page: 1,
      totalPages: 1,
      hasNext: false,
      hasPrevious: false,
    });

    // Call fetchData directly without adding it to deps
    fetchData({ page: 1, mode: "initial" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCloud, trigger]);

  // Throttled infinite scroll handler - FIXED: use ref for pagination to avoid recreating
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const handleScrollEnd = useCallback(() => {
    // SIMPLE FIX: Ignore the first scroll event (AG Grid auto-scroll on mount)
    scrollCount.current++;
    if (scrollCount.current === 1) {
      console.log("🚫 Ignored first scroll event (AG Grid auto-scroll)");
      return;
    }

    const now = Date.now();
    if (now - lastScrollTs.current < SCROLL_THROTTLE_MS) {
      return;
    }
    lastScrollTs.current = now;

    const gridApi = gridRef.current?.api;
    if (!gridApi) {
      return;
    }

    try {
      // Get the total row count from the data
      const totalRowCount = misConfigData.length;
      if (totalRowCount === 0) return;

      // Get viewport info from the event or API
      const verticalPixelRange = gridApi.getVerticalPixelRange?.();

      if (!verticalPixelRange) {
        // Fallback: use simple row count check
        const currentPagination = paginationRef.current;

        // If we have all data loaded and user scrolled, check if we need more
        if (currentPagination.hasNext && !isFetchingNext.current) {
          console.log("⬇️ Loading next page:", currentPagination.page + 1);
          isFetchingNext.current = true;
          fetchData({ page: currentPagination.page + 1, mode: "append" });
        }
        return;
      }

      // Calculate scroll position
      const { top, bottom } = verticalPixelRange;
      const scrollTop = top;
      const scrollHeight =
        gridApi.getModel?.()?.getRowCount?.() * 50 || totalRowCount * 50; // Assume 50px row height
      const clientHeight = bottom - top;

      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
      const isNearBottom = scrollPercentage > 0.8; // 80% scrolled
      const isNearTop = scrollTop < 100; // Less than 100px from top

      const currentPagination = paginationRef.current;

      console.log("📜 Scroll:", {
        scrollTop,
        scrollHeight,
        clientHeight,
        scrollPercentage: (scrollPercentage * 100).toFixed(1) + "%",
        isNearBottom,
        isNearTop,
        hasNext: currentPagination.hasNext,
        hasPrev: currentPagination.hasPrevious,
      });

      // Load next page when near bottom
      if (
        isNearBottom &&
        currentPagination.hasNext &&
        !isFetchingNext.current
      ) {
        console.log("⬇️ Loading next page:", currentPagination.page + 1);
        isFetchingNext.current = true;
        fetchData({ page: currentPagination.page + 1, mode: "append" });
        return;
      }

      // Load previous page when near top
      if (
        isNearTop &&
        currentPagination.hasPrevious &&
        !isFetchingPrev.current
      ) {
        console.log("⬆️ Loading previous page:", currentPagination.page - 1);
        isFetchingPrev.current = true;
        fetchData({ page: currentPagination.page - 1, mode: "prepend" });
        return;
      }
    } catch (err) {
      console.error("Scroll handler error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [misConfigData.length]);

  // Drawer open effect
  useEffect(() => {
    if (isDrawerOpen && selectedRow?.id) {
      setIsDrawerDataLoading(true);
      Promise.all([fetchControlDetails(), fetchRiskDetails()]).finally(() => {
        setIsDrawerDataLoading(false);
      });
    } else if (!isDrawerOpen) {
      setHeaderResponse(null);
      setRiskDetails([]);
      setIsCreateTags({});
      setShowLogOutput(false);
      setLogOutputRowData(null);
      setValue(0);
      setIsDetailsTotalResources(null);
    }
  }, [isDrawerOpen, selectedRow?.id, fetchControlDetails, fetchRiskDetails]);

  const onRowClick = (data: SelectedRowData) => {
    setSelectedRow(data);
    dispatch(setSelectedRowID(data.id));
    setIsDrawerOpen(true);
  };

  const getImage = () => {
    if (isHovered || open) return Pencil_Blue;
    return Pencil_Light_img;
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsCreateTagClicked(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const getSeverityStyle = useCallback((severity: string) => {
    switch (severity?.toLowerCase()) {
      case "low":
        return { color: "#F2BF00", backgroundColor: "#F2BF0024" };
      case "high":
        return { color: "#A7000F", backgroundColor: "#A7000F24" };
      case "critical":
        return { color: "#670004", backgroundColor: "#67000424" };
      case "medium":
        return { color: "#E85800", backgroundColor: "#E8580024" };
      default:
        return { color: "gray", backgroundColor: "#E0E0E0" };
    }
  }, []);

  const Chipstyle = getSeverityStyle(
    (headerResponse?.severity as string) || ""
  );
  const Total_Resources = isDetailsTotalResources?.data;

  // Show user-created tags first so newly created tags appear at the top
  const allTags = [
    ...(headerResponse?.tags?.user || []),
    ...(headerResponse?.tags?.default || []),
  ];

  const visibleTags = allTags.slice(0, 3);
  const hiddenTags = allTags.slice(3);

  const TypeChipColor = useCallback((type: string) => {
    switch (type) {
      case "Manual":
        return { color: "#2B335F", bgcolor: "#DEDDF2" };
      case "Automated":
        return { color: "#0B3D2E", bgcolor: "#CFF3E0" };
      default:
        return { color: "#000", bgcolor: "#E0E0E0" };
    }
  }, []);

  const getStyledTag = (tag: string, index: number) => {
    const tagStyles = [
      { color: "#018319", backgroundColor: "#0183191A" },
      { color: "#894B00", backgroundColor: "#894B001A" },
      { color: "#0485C2", backgroundColor: "#0485C21A" },
      { color: "#3A2ADA", backgroundColor: "#1103C014" },
      { color: "gray", backgroundColor: "#E0E0E0" },
    ];

    const style = tagStyles[index] || {
      color: "black",
      backgroundColor: "#f0f0f0",
    };
    return (
      <Chip
        key={index}
        label={tag}
        style={style}
        sx={{ borderRadius: 1, fontWeight: "bold" }}
        size="small"
      />
    );
  };

  const formik = useFormik({
    initialValues: {
      create_tag: "",
    },
    onSubmit: async (values) => {
      await axiosInstance
        .post("securityscan/create-tag", {
          tag_name: values.create_tag,
          parent_id: null,
          parameter_name_id: selectedRow?.id,
        })
        .then((response) => {
          toast(response?.data?.message, { type: "success" });
          formik.resetForm();
          setIsCreateTagClicked(false);
          handleClose();
          fetchControlDetails();
          fetchData({ page: 1, mode: "initial" });
        })
        .catch((error) => {
          toast(error?.response?.data?.message || "Something went wrong", {
            type: "error",
          });
        });
    },
  });

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    dispatch(setTabSelected(newValue));
  };

  const memoizedGridOptions = useMemo(
    () => ({
      rowHeight: 50,
      domLayout: "normal",
      // Prevent AG Grid from auto-scrolling on new data so we can restore manually
      suppressScrollOnNewData: true,
      // Hide the sort order numbers (1, 2, etc.) in column headers
      suppressMultiSort: true,
      onBodyScrollEnd: handleScrollEnd,
      headerClass:
        theme.palette.mode === "dark"
          ? agGridStyles["ag-header-dark"]
          : agGridStyles["ag-header-light"],
      rowClass:
        theme.palette.mode === "dark"
          ? agGridStyles["ag-row-dark"]
          : agGridStyles["ag-row-light"],
      fullWidthCellRenderer: () => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ),
      isFullWidthRow: (params: any) => params.rowNode?.data?._isLoadingRow === true,
    }),
    [theme.palette.mode, handleScrollEnd]
  );
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  // CLIENT-SIDE FILTERING DISABLED - Backend handles all filtering/sorting
  // TODO: Move filter to backend API once implemented
  const filteredData = useMemo(() => {
    // const filtered = misConfigData.filter((item: DataProps) => {
    //   if (filterBy === "all") return true;
    //   return item.vulnerability.toLowerCase() === filterBy.toLowerCase();
    // });
    console.log("🔍 Using raw data (no client-side filter):", {
      total: misConfigData.length,
      filterBy: "disabled",
      sample: misConfigData[0],
    });
    return misConfigData; // Use raw data from backend
  }, [misConfigData]);

  // Add loading row at the end when fetching more pages
  const displayData = useMemo(() => {
    if (isFetchingMore) {
      return [...filteredData, { _isLoadingRow: true }];
    }
    return filteredData;
  }, [filteredData, isFetchingMore]);

  // Calculate total records info
  const recordsInfo = useMemo(() => {
    const loadedCount = misConfigData.length;
    const filteredCount = filteredData.length;
    const totalAvailable = pagination.totalPages * PAGE_SIZE;
    const loadedPages = Math.ceil(loadedCount / PAGE_SIZE);

    return {
      loadedCount,
      filteredCount,
      totalAvailable,
      loadedPages,
      totalPages: pagination.totalPages,
      showingFiltered: filterBy !== "all",
    };
  }, [
    misConfigData.length,
    filteredData.length,
    pagination.totalPages,
    filterBy,
  ]);

  return (
    <div data-theme={theme.palette.mode}>
      <div className={styles.container}>
        <div className={styles.header} style={{ position: "relative" }}>
          <h2 style={{ width: "100%", marginRight: "10px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div>{"Top Misconfiguration"}</div>
                <div
                  style={{
                    fontSize: "12px",
                    color: theme.palette.mode === "dark" ? "#A6A5B6" : "#666",
                    fontWeight: 400,
                  }}
                >
                  {/* Record count display commented out per UX request.
                  {recordsInfo.showingFiltered ? (
                    <>
                      Showing {recordsInfo.filteredCount} of {recordsInfo.loadedCount} loaded
                      {" • "}Total: {recordsInfo.totalAvailable} records
                    </>
                  ) : (
                    <>
                      Loaded {recordsInfo.loadedCount} of {recordsInfo.totalAvailable} records
                      {" • "}Pages {recordsInfo.loadedPages}/{recordsInfo.totalPages}
                    </>
                  )}
                  */}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>On-Prem</span>
                <AntSwitch
                  checked={Boolean(isCloud)}
                  onChange={(_, checked) => {
                    setIsCloud(checked);
                    setFilterBy(filterOptions[0].value ?? filterOptions[0]);
                  }}
                  disabled={switchDisabled}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: "#FFFFFF",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#3A2ADA",
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#3A2ADA",
                    },
                  }}
                />
                <span>Cloud</span>
              </div>
            </div>
          </h2>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            {/* CLIENT-SIDE FILTER DISABLED - Backend handles filtering */}
            {/* <SortDropdown
              options={filterOptions}
              setSortBy={setFilterBy}
              sortBy={filterBy}
            /> */}
          </div>
        </div>
        <div className={styles.data_container}>
          {loading ? (
            <SkeletonLoader
              count={5}
              styles={{
                height: memoizedGridOptions.rowHeight,
                width: "100%",
              }}
            />
          ) : (
            <>
              <AgGridTable
                columnDefs={columnDefs}
                data={displayData}
                gridRef={gridRef}
                showSearch={false}
                isRowSelectable={false}
                pagination={false}
                showAppsIcon={false}
                gridOptions={memoizedGridOptions}
                isLoading={loading}
                styles={agGridInlineStyles}
                onRowClick={(data: any) => {
                  // Don't trigger row click for loading row
                  if (!data._isLoadingRow) {
                    onRowClick(data);
                  }
                }}
                height={"99%"}
                scrollDelay={0}
              />
              {/* Loading indicator for pagination - handled in-table via displayData loading row */}
              {/* {isFetchingMore && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 1,
                  }}
                >
                  <CircularProgress size={18} />
                </Box>
              )} */}
            </>
          )}
        </div>
      </div>

      {/* Rest of your drawer code remains the same */}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: "50%",
            overflow: "hidden",
            bgcolor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
          },
        }}
      >
        {isDrawerDataLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {/* <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading details...
            </Typography> */}
            <SkeletonLoader
              count={Math.floor(window.innerHeight / 40)}
              styles={{ height: 30, width: "100%" }}
            />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                padding: 2,
                borderBottom: 1,
                borderColor: "divider",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {(headerResponse?.normalized_name as string) || ""}
                </Typography>
                <HighlightOffIcon
                  sx={{ color: "#A6A5B6", cursor: "pointer" }}
                  onClick={() => {
                    setShowLogOutput(false);
                    setIsDrawerOpen(false);
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 5,
                  marginBottom: 3,
                }}
              >
                <Chip
                  icon={
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: Chipstyle.backgroundColor,
                        borderRadius: "50%",
                      }}
                    >
                      <FiberManualRecordIcon
                        fontSize="inherit"
                        sx={{ color: Chipstyle.color }}
                      />
                    </Box>
                  }
                  label={
                    headerResponse?.severity
                      ? String(headerResponse.severity)
                      : ""
                  }
                  size="small"
                  sx={{
                    fontWeight: "bold",
                    ...Chipstyle,
                    borderRadius: 1,
                  }}
                />
                <Chip
                  label={
                    headerResponse?.type ? String(headerResponse.type) : ""
                  }
                  sx={{
                    borderRadius: 1,
                    bgcolor: TypeChipColor(
                      (headerResponse?.type as string) || ""
                    ).bgcolor,
                    color: TypeChipColor((headerResponse?.type as string) || "")
                      .color,
                  }}
                  size="small"
                />
                <Divider orientation="vertical" flexItem />
                <Typography
                  sx={{ fontWeight: 600, fontSize: "14px", color: "#8787AE" }}
                >
                  Tags:
                </Typography>
                <div style={{ display: "flex", flexDirection: "row", gap: 5 }}>
                  {visibleTags.map((tag: string, index: number) => {
                    const formattedTag = tag
                      ? String(tag).charAt(0).toUpperCase() +
                      String(tag).slice(1)
                      : "";

                    const displayTag =
                      formattedTag.length > 7
                        ? formattedTag.slice(0, 7) + "…"
                        : formattedTag;

                    return getStyledTag(displayTag, index);
                  })}

                  {hiddenTags.length > 0 && (
                    <Tooltip
                      title={hiddenTags
                        .map((tag) =>
                          tag
                            ? String(tag).charAt(0).toUpperCase() +
                            String(tag).slice(1)
                            : ""
                        )
                        .join(", ")}
                      arrow
                      placement="top"
                    >
                      <div
                        style={{
                          backgroundColor: "#ccc",
                          borderRadius: "50%",
                          width: 28,
                          height: 28,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold",
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        +{hiddenTags.length}
                      </div>
                    </Tooltip>
                  )}
                </div>
                <div>
                  <img
                    src={getImage()}
                    alt="edit"
                    style={{ cursor: "pointer", width: "25px", marginLeft: 10 }}
                    onClick={handleClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  />

                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "center",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "center",
                    }}
                    sx={{
                      "& .MuiPopover-paper": {
                        boxShadow: 1,
                        mt: 1,
                      },
                    }}
                  >
                    {" "}
                    {isCreateTagClicked ? (
                      <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ bgcolor: "background.paper", p: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="700"
                            sx={{ mb: 1 }}
                          ></Typography>
                          <TextField
                            variant="outlined"
                            sx={{ mb: 2 }}
                            placeholder="Create Tag"
                            id="create_tag"
                            name="create_tag"
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            value={formik.values.create_tag}
                            required
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Button
                              variant="outlined"
                              sx={{ width: 90 }}
                              onClick={handleClose}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              sx={{ width: 90 }}
                              type="submit"
                            >
                              Create
                            </Button>
                          </div>
                        </Box>
                      </form>
                    ) : (
                      <Box sx={{ p: 1, bgcolor: "background.paper" }}>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          placeholder="Search"
                          sx={{
                            mb: 1,
                            color: "#E9E9F8",
                            backgroundClip: "#E9E9F8",
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                        />{" "}
                        <Divider sx={{ color: "#D2D2F6" }} />
                        <div
                          style={{
                            display: "flex",
                            gap: 5,
                            marginTop: 10,
                            marginBottom: 10,
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={Plus_Blue_img}
                            alt=""
                            style={{ width: 30, cursor: "pointer" }}
                            onClick={() => setIsCreateTagClicked(true)}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#3A2ADA",
                              fontSize: "16px",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            onClick={() => setIsCreateTagClicked(true)}
                          >
                            Create Tag
                          </Typography>
                        </div>
                        <Divider sx={{ color: "#D2D2F6" }} />
                        <div>
                          {/* User Tags - show first so newly created tags appear at top */}
                          <Box
                            display="flex"
                            flexDirection="column"
                            gap="0.5rem"
                            mb={isCreateTag?.default?.length ? 2 : 0}
                          >
                            {isCreateTag?.user && isCreateTag.user.length > 0 ? (
                              isCreateTag.user.map((tag: string, index: number) => (
                                <Tooltip
                                  key={index}
                                  title={tag.length > 10 ? tag : ""}
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        boxShadow: 1,
                                        fontSize: "0.775rem",
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#fff",
                                      },
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "16px",
                                      color: "#6D738F",
                                      maxWidth: "150px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {tag}
                                  </Typography>
                                </Tooltip>
                              ))
                            ) : (
                              <div></div>
                            )}
                          </Box>

                          {/* Default Tags - show after user tags */}
                          <Box display="flex" flexDirection="column" gap="0.5rem">
                            {isCreateTag?.default && isCreateTag.default.length > 0 ? (
                              isCreateTag.default.map((tag: string, index: number) => (
                                <Tooltip
                                  key={index}
                                  title={tag.length > 10 ? tag : ""}
                                  arrow
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        backgroundColor: "#fff",
                                        color: "#000",
                                        boxShadow: 1,
                                        fontSize: "0.775rem",
                                      },
                                    },
                                    arrow: {
                                      sx: {
                                        color: "#fff",
                                      },
                                    },
                                  }}
                                >
                                  <Typography
                                    key={`default-${index}`}
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      fontSize: "16px",
                                      color: "#6D738F",
                                      maxWidth: "150px",
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {tag}
                                  </Typography>
                                </Tooltip>
                              ))
                            ) : (
                              <div></div>
                            )}
                          </Box>
                        </div>
                      </Box>
                    )}
                  </Popover>
                </div>{" "}
              </div>
            </Box>

            {/* {showLogOutput?():()} */}

            <Box sx={{ flexGrow: 1, overflowY: "auto" }} className="scroll">
              {showLogOutput ? (
                <Logoutput
                  rowData={logOutputRowData}
                  parameter_name_id={selectedRow?.id ?? ""}
                  onBack={() => setShowLogOutput(false)}
                  module={isCloud ? "cloud" : "on_premise"}
                />
              ) : (
                <>
                  <Box sx={{ position: "sticky", zIndex: 9 }}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="basic tabs example"
                      >
                        {(() => {
                          const tabsList = [
                            { label: "Assets", index: 0 },
                            { label: "Compliance", index: 1 },
                            { label: "Details", index: 2 },
                          ];
                          
                          let visibleTabs = tabsList;
                          
                          const paramType = selectedRow?.parameter_type?.toLowerCase();
                          if (paramType === "template" || paramType === "custom") {
                            visibleTabs = visibleTabs.filter(tab => tab.label !== "Compliance");
                          }

                          return visibleTabs.map((tab) => (
                            <Tab
                              key={tab.index}
                              value={tab.index}
                              label={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: "bold",
                                    color:
                                      value === tab.index ? "#3A2ADA" : "#8787AE",
                                  }}
                                >
                                  {tab.label}
                                </Typography>
                              }
                              {...a11yProps(tab.index)}
                              sx={{
                                minWidth: 100,
                                "&.Mui-selected": { color: "#3A2ADA" },
                                "&:not(.Mui-selected)": { color: "#8787AE" },
                                textTransform: "none",
                              }}
                            />
                          ));
                        })()}
                      </Tabs>
                    </Box>
                  </Box>

                  <CustomTabPanel value={value} index={0}>
                    <AssetsFlyout
                      id={selectedRow?.id ?? ""}
                      onRowClick={(row: any) => {
                        console.log(row);
                        setShowLogOutput(true);
                        setLogOutputRowData(row);
                      }}
                      module={isCloud ? "cloud" : "on_premise"}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={value} index={1}>
                    <Compliance
                      id={selectedRow?.id ?? ""}
                      module={isCloud ? "cloud" : "on_premise"}
                    />
                  </CustomTabPanel>
                  <CustomTabPanel value={value} index={2}>
                    <DetailsFlyout
                      Total_Resources={Total_Resources}
                      id={selectedRow?.id ?? ""}
                      module={isCloud ? "cloud" : "on_premise"}
                      parameterType={selectedRow?.parameter_type}
                    />
                  </CustomTabPanel>
                </>
              )}
            </Box>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default TopMisconfiguration;
